import { Signal, effect, untracked, useComputed } from "@suse-o11y/signal";
import React from "react";
import { useSelector } from "react-redux";

import { usePermanent } from "~/tools/use-permanent";

import { selectTimeRange } from "../../../app/state/view/timeline/TimelineSelectors";
import { DEFAULT_TIME_RANGE, type SpecificRange } from "../../../app/state/view/timeline/TimeRange";
import { useNavigateFrom } from "../../../components/react/link";

import { DashboardContextProvider, type DashboardContextValue } from "./_dashboard-context";
import { DashboardState } from "./_dashboard-state";
import { useDashboardVariables } from "./_use-dashboard-variables";
import { DashboardVariablesBoundary } from "./variables-boundary";

export const DashboardProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const navigateFrom = useNavigateFrom();

	const timeRangeFromUrl = useSelector(selectTimeRange) ?? DEFAULT_TIME_RANGE;

	const state = usePermanent(() => new DashboardState());
	const chartTimeRange = usePermanent(() =>
		Signal<SpecificRange>(timeRangeFromUrl.toSpecific(), {
			equals: (left, right) => left.isEqual(right),
		}),
	);

	const timeRange = React.useMemo(
		() =>
			Signal(
				(monitor) => state.form.fields.settings.fields.timeRange.getInput(monitor) ?? timeRangeFromUrl,
				(next, monitor) => {
					if (state.form.fields.settings.fields.timeRange.getInput(monitor) != null) {
						state.form.fields.settings.fields.timeRange.setInput(next);
					} else {
						navigateFrom("*", {
							search: (params) => ({ ...params, timeRange: next }),
						});
					}
				},
				{
					equals: (left, right) => left.isEqual(right),
				},
			),
		[navigateFrom, state, timeRangeFromUrl],
	);

	React.useLayoutEffect(
		() =>
			effect((monitor) => {
				chartTimeRange.write(timeRange.read(monitor).toSpecific());
			}),
		[chartTimeRange, timeRange],
	);

	const refreshChartTimeRange = React.useCallback(() => {
		untracked((monitor) => {
			chartTimeRange.write(timeRange.read(monitor).toSpecific());
		});
	}, [chartTimeRange, timeRange]);

	const [variables, onVariablesChange] = useDashboardVariables(state);

	const context = React.useMemo(
		(): DashboardContextValue => ({
			timeRange,
			chartTimeRange,
			refreshChartTimeRange,
			state,
			variables: Signal(() => variables, onVariablesChange),
		}),
		[timeRange, chartTimeRange, refreshChartTimeRange, state, variables, onVariablesChange],
	);

	return (
		<DashboardContextProvider value={context}>
			<DashboardVariablesBoundary timeRange={useComputed(chartTimeRange)} variables={variables}>
				{children}
			</DashboardVariablesBoundary>
		</DashboardContextProvider>
	);
};
